import { InfoCircleOutlined } from '@ant-design/icons';
import { COLORS } from '_/lib/frontend-utils';
import { Typography, Alert, Button, Popover, Switch, Space, Card, Input, Form } from 'antd';
import { toString } from 'cronstrue';
import React, { ChangeEvent, useEffect, useState } from 'react';

import classes from './FlowCronExpression.module.css';

const defaultCronValues = ['', '', '', '', ''];

const checkCronFormat = (cron) => {
  if (Array.isArray(cron)) {
    cron = cron.filter((item) => item);
    return cron.length === 5;
  }
  return false;
};

const formatCron = (cron) => (checkCronFormat(cron) ? toString(cron.join(' ')) : '');

const COMMON_CRON_RULES = [
  { symbol: '*', description: 'any value' },
  { symbol: ',', description: 'value list separator' },
  { symbol: '-', description: 'range of values' },
  { symbol: '/', description: 'step values' },
];

const CRON_FIELD_CONFIG = {
  cronMin: {
    title: 'Minutes',
    rules: [...COMMON_CRON_RULES, { symbol: '0-59', description: 'allowed values' }],
  },
  cronHours: {
    title: 'Hours',
    rules: [...COMMON_CRON_RULES, { symbol: '0-23', description: 'allowed values' }],
  },
  cronDayMonth: {
    title: 'Day (month)',
    rules: [...COMMON_CRON_RULES, { symbol: '1-31', description: 'allowed values' }],
  },
  cronMonth: {
    title: 'Month',
    rules: [
      ...COMMON_CRON_RULES,
      { symbol: '1-12', description: 'allowed values' },
      { symbol: 'jan-dec', description: 'alternative single values' },
    ],
  },
  cronDayWeek: {
    title: 'Day (Week)',
    rules: [
      ...COMMON_CRON_RULES,
      { symbol: '0-6', description: 'allowed values' },
      { symbol: 'sun-sat', description: 'alternative single values' },
    ],
  },
};

const renderCronHelper = (fieldConfig) => {
  if (!fieldConfig) return null;

  return (
    <Alert
      type="info"
      message={fieldConfig.title}
      description={
        <div>
          {fieldConfig.rules.map((rule, index) => (
            <div key={index} className={classes.cronDictionaryFlexRow}>
              <div className={`${classes.boldText} ${classes.cronVal}`}>{rule.symbol}</div>
              <div className={classes.marginLeft}>{rule.description}</div>
            </div>
          ))}
        </div>
      }
    />
  );
};

interface FlowCronExpressionProps {
  flow: {
    cron: string[];
    cronScheduling: boolean;
  };
  handleCronChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleFieldChange: (
    event: ChangeEvent<HTMLInputElement> | { target: { name: string; value?: any; checked?: boolean; type?: string } },
  ) => void;
  isValidCronExpression: boolean;
  resetCronValues: (values: string[]) => void;
}

function FlowCronExpression({
  flow,
  handleCronChange,
  handleFieldChange,
  isValidCronExpression,
  resetCronValues,
}: FlowCronExpressionProps) {
  const [openCronPopover, setOpenCronPopover] = useState(false);
  const [cronField, setCronField] = useState('cronMin');
  const { cron, cronScheduling } = flow;
  const [minutes, hours, dayOfMonth, month, dayOfWeek] = cron;
  const cronDisplay = isValidCronExpression ? formatCron([minutes, hours, dayOfMonth, month, dayOfWeek]) : '';

  useEffect(() => {
    if (!cronScheduling) {
      resetCronValues(defaultCronValues);
    }
  }, [cronScheduling, resetCronValues]);

  const handleCronPopover = () => {
    setOpenCronPopover(true);
  };

  const closeCronPopover = () => {
    setOpenCronPopover(false);
  };

  const updateCronHelper = (event) => {
    event.preventDefault();
    setCronField(event.target.id);
  };

  const toggleCronExpressionFields = (checked) => {
    handleFieldChange({
      target: { name: 'cronScheduling', checked: checked, type: 'checkbox' },
    } as any);
  };

  const cronHelper = renderCronHelper(CRON_FIELD_CONFIG[cronField], classes);

  return (
    <div>
      <Space>
        <Switch checked={cronScheduling} onChange={toggleCronExpressionFields} />
        <div>Do you want to add scheduling?</div>
      </Space>
      {cronScheduling && (
        <Card style={{ marginTop: '25px' }} styles={{ body: { padding: '10px' } }}>
          <Space direction="vertical">
            <Space>
              <span className={classes.boldText}>Cron Expression:</span>
              <Space direction="vertical">
                <Space>
                  <Form.Item
                    name="cronMin"
                    rules={[
                      {
                        required: true,
                        message: 'Minutes is required',
                      },
                      {
                        pattern:
                          /^(\*|([0-9]|[1-5][0-9])(,([0-9]|[1-5][0-9]))*|([0-9]|[1-5][0-9])-([0-9]|[1-5][0-9])|\*\/([0-9]|[1-5][0-9]))$/,
                        message: 'Invalid format (0-59, *, -, /, ,)',
                      },
                    ]}
                  >
                    <Input
                      id="cronMin"
                      placeholder="Minutes"
                      name="0"
                      aria-describedby="project-flow-cron-min"
                      onChange={handleCronChange}
                      onFocus={updateCronHelper}
                      value={minutes ?? ''}
                      style={{ width: 120 }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="cronHours"
                    rules={[
                      {
                        required: true,
                        message: 'Hours is required',
                      },
                      {
                        pattern:
                          /^(\*|([0-9]|1[0-9]|2[0-3])(,([0-9]|1[0-9]|2[0-3]))*|([0-9]|1[0-9]|2[0-3])-([0-9]|1[0-9]|2[0-3])|\*\/([0-9]|1[0-9]|2[0-3]))$/,
                        message: 'Invalid format (0-23, *, -, /, ,)',
                      },
                    ]}
                  >
                    <Input
                      id="cronHours"
                      placeholder="Hours"
                      name="1"
                      aria-describedby="project-flow-cron-hrs"
                      onChange={handleCronChange}
                      onFocus={updateCronHelper}
                      value={hours ?? ''}
                      style={{ width: 120 }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="cronDayMonth"
                    rules={[
                      {
                        required: true,
                        message: 'Day (Month) is required',
                      },
                      {
                        pattern:
                          /^(\*|([1-9]|[12][0-9]|3[01])(,([1-9]|[12][0-9]|3[01]))*|([1-9]|[12][0-9]|3[01])-([1-9]|[12][0-9]|3[01])|\*\/([1-9]|[12][0-9]|3[01]))$/,
                        message: 'Invalid format (1-31, *, -, /, ,)',
                      },
                    ]}
                  >
                    <Input
                      id="cronDayMonth"
                      placeholder="Day (Month)"
                      name="2"
                      aria-describedby="project-flow-cron-day"
                      onChange={handleCronChange}
                      onFocus={updateCronHelper}
                      value={dayOfMonth ?? ''}
                      style={{ width: 120 }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="cronMonth"
                    rules={[
                      {
                        required: true,
                        message: 'Month is required',
                      },
                      {
                        pattern:
                          /^(\*|([1-9]|1[0-2])(,([1-9]|1[0-2]))*|([1-9]|1[0-2])-([1-9]|1[0-2])|\*\/([1-9]|1[0-2])|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)$/i,
                        message: 'Invalid format (1-12, jan-dec, *, -, /, ,)',
                      },
                    ]}
                  >
                    <Input
                      id="cronMonth"
                      placeholder="Month"
                      name="3"
                      aria-describedby="project-flow-cron-month"
                      onChange={handleCronChange}
                      onFocus={updateCronHelper}
                      value={month ?? ''}
                      style={{ width: 120 }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="cronDayWeek"
                    rules={[
                      {
                        required: true,
                        message: 'Day (Week) is required',
                      },
                      {
                        pattern: /^(\*|[0-6](,[0-6])*|[0-6]-[0-6]|\*\/[0-6]|sun|mon|tue|wed|thu|fri|sat)$/i,
                        message: 'Invalid format (0-6, sun-sat, *, -, /, ,)',
                      },
                    ]}
                  >
                    <Input
                      id="cronDayWeek"
                      placeholder="Day (Week)"
                      name="4"
                      aria-describedby="project-flow-cron-day-week"
                      onChange={handleCronChange}
                      onFocus={updateCronHelper}
                      value={dayOfWeek ?? ''}
                      style={{ width: 120 }}
                    />
                  </Form.Item>
                </Space>
              </Space>
              <Popover
                open={openCronPopover}
                onOpenChange={(visible) => {
                  if (!visible) closeCronPopover();
                }}
                content={
                  <Typography.Text className={classes.typography}>
                    Cron expressions should follow the format of:
                    <br />
                    (Minute) (Hour) (Day of Month) (Month) (Day of Week)
                    <br />
                  </Typography.Text>
                }
                placement="right"
              >
                <Button
                  type="text"
                  shape="circle"
                  onClick={handleCronPopover}
                  icon={<InfoCircleOutlined style={{ color: COLORS.DARK_BLUE }} />}
                />
              </Popover>
            </Space>
            {cronDisplay && <Alert type="success" title={cronDisplay} style={{ fontSize: '13px' }} />}
            {cronScheduling && cronHelper}
          </Space>
        </Card>
      )}
    </div>
  );
}

export default FlowCronExpression;
